import type { ComponentProps } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";

/**
 * Herald chat catalog definition for Button. Use in renderer catalog.
 * External URLs belong on `Link` (`ChatLinkSpec`), not Button.
 */
export const ChatButtonSpec = {
  props: z.object({
    label: z.string(),
    variant: z
      .enum(["default", "secondary", "destructive", "outline", "ghost"])
      .nullable()
      .optional(),
    size: z.enum(["default", "sm", "lg"]).nullable().optional(),
    disabled: z.boolean().nullable().optional(),
  }),
  description:
    "Clickable button. Use with on.press to trigger actions like setState. Do not use Button for URLs — use Link (text + href). In chat, buttons render compact (text-xs, h-7).",
  example: {
    label: "Apply filters",
    variant: "outline",
    size: "sm",
    disabled: false,
  },
} as const;

/** Compact styles for spec buttons in Herald chat (overrides default size height). */
const CHAT_SPEC_BUTTON_CLASS = "h-7 min-h-0 px-2.5 py-0 text-xs font-medium";

/**
 * App `Button` adapter for defineRegistry: `onPress` → `emit("press")` so spec `on.press` binds to the "press" action name.
 */
export const ChatButtonFromProps = ({
  props,
  emit,
}: {
  props: Record<string, unknown>;
  emit: (event: string) => void;
}) => {
  const { label, variant, size, disabled } = props as {
    label: string;
    variant?: string | null;
    size?: string | null;
    disabled?: boolean | null;
  };

  return (
    <Button
      variant={variant as ComponentProps<typeof Button>["variant"] | undefined}
      size={size as ComponentProps<typeof Button>["size"] | undefined}
      type="button"
      isDisabled={disabled === true}
      className={CHAT_SPEC_BUTTON_CLASS}
      onPress={() => emit("press")}
    >
      {label}
    </Button>
  );
};
